import React from 'react';
import { useLanguage } from '../../contexts/languageContext';
import { useTheme } from '../../contexts/themeContext';
import { Navbar } from 'react-bootstrap';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faCommentDots, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './navbar.scss';
import { trackEvent } from '../../lib/analytics';
import { shouldShowEntryPoints as showChatEntry } from '../cv-chat/cvChatClient';

const NavbarComponent: React.FC = () => {
  const { language, t, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // I toggle conservano la rotta corrente: con il pathname fisso a '/' cambiare
  // lingua o tema da /talktome riportava alla home.
  const currentPathname = location.pathname;

  const handleToggleLanguage = () => {
    const newLanguage = language === 'en' ? 'it' : 'en';
    const current = new URLSearchParams(searchParams);
    current.set('lang', newLanguage);
    current.set('theme', theme);
    navigate({ pathname: currentPathname, search: `?${current.toString()}` }, { replace: true });
    toggleLanguage();
  };

  const handleToggleTheme = () => {
    const newTheme = theme === 'business' ? 'casual' : 'business';
    const current = new URLSearchParams(searchParams);
    current.set('theme', newTheme);
    current.set('lang', language);
    navigate({ pathname: currentPathname, search: `?${current.toString()}` }, { replace: true });
    toggleTheme();
  };

  const isOnChatPage = currentPathname === '/talktome';

  /** Naviga conservando tema e lingua correnti. */
  const goTo = (pathname: string, source: string) => {
    try {
      trackEvent(pathname === '/talktome' ? 'talktome_entry_click' : 'talktome_back_click', {
        source,
      });
    } catch {
      // l'analytics non deve impedire la navigazione
    }
    navigate({ pathname, search: `?${searchParams.toString()}` });
  };

  return (
    <Navbar
      expand="lg"
      className={`fixed-top p-1 no-print ${isOnChatPage ? 'navbar-talktome' : ''}`.trim()}
    >
      <div className="d-flex ms-4 w-100">
        {/* Switch per la lingua */}
        <div className="form-check form-switch form-control-lg d-flex align-items-center me-6">
          <input
            className="form-check-input form-control-lg custom-toggle"
            type="checkbox"
            id="languageSwitch"
            checked={language === 'it'}
            onChange={handleToggleLanguage}
            role="switch"
            aria-label="Toggle language"
          />
          <label className="form-check-label ms-2 text-light" htmlFor="languageSwitch">
            {language === 'en' ? 'English' : 'Italiano'}
          </label>
        </div>

        {/* Switch per il tema */}
        <div className="form-check mr-auto form-switch form-control-lg d-flex align-items-center">
          <input
            className="form-check-input form-control-lg custom-toggle"
            type="checkbox"
            id="themeSwitch"
            checked={theme === 'casual'}
            onChange={handleToggleTheme}
            role="switch"
            aria-label="Toggle theme"
          />
          <label className="form-check-label ms-2 text-light" htmlFor="themeSwitch">
            {theme === 'business' ? 'Business' : 'Casual'}
          </label>
        </div>

        {/*
          Gruppo di destra: ms-auto sta sul contenitore e non sui singoli
          pulsanti. Con ms-auto condizionale su ogni pulsante, l'allineamento
          dipendeva da quali erano visibili.

          Sulla pagina della chat restano fuori sia il fumetto (ci si e' già)
          sia la stampa (quella pagina non e' stampabile): al loro posto compare
          il ritorno al CV.
        */}
        <div className="ms-auto me-4 d-flex align-items-center">
          {isOnChatPage ? (
            <button
              type="button"
              className="btn btn-lg text-primary d-flex align-items-center gap-2"
              onClick={() => goTo('/', 'navbar')}
              title={t('chatBackToCv')}
              aria-label={t('chatBackToCv')}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="fs-6 d-none d-sm-inline">{t('chatBackToCv')}</span>
            </button>
          ) : (
            <>
                {/* Chat CV. In produzione compare solo con il backend
                  configurato, per non offrire un ingresso verso una pagina non
                  funzionante. */}
              {showChatEntry() && (
                <button
                  type="button"
                  className="btn btn-lg text-primary"
                  onClick={() => goTo('/talktome', 'navbar')}
                  title={t('talkToMeNavLabel')}
                  aria-label={t('talkToMeNavLabel')}
                >
                  <FontAwesomeIcon icon={faCommentDots} />
                </button>
              )}

              <button
                type="button"
                className="btn btn-lg text-primary"
                onClick={() => {
                  try {
                    trackEvent('print_click');
                  } catch {
                    // noop
                  }
                  window.print();
                }}
                title="Print"
                aria-label="Print"
              >
                <FontAwesomeIcon icon={faPrint} />
              </button>
            </>
          )}
        </div>
      </div>
    </Navbar>
  );
};

export default NavbarComponent;
